<template>
  <ModalDialog
    ref="modal"
    data-testid="review-reply-dialog"
    persistent
    max-height="95vh"
    max-width="648px"
    :title="t('Reviews.reviewReplyDialog.title')"
    @closed="$emit('closed')"
  >
    <div class="mb-6 text-body-s">{{ t('Reviews.reviewReplyDialog.text') }}</div>
    <MessageBanner class="my-6" :message="t('Reviews.sendReplyDialog.text')" type="info" />
    <div class="pb-1 text-body-m-bold">
      <span v-if="v$.personalized.$model">{{
        t('Reviews.reviewReply.sectionTitleWithAuthor', { authorName: senderName, entityTitle })
      }}</span>
      <span v-else>{{ t('Reviews.reviewReply.sectionTitle', { entityTitle }) }}</span>
    </div>
    <div class="mb-4">
      <CheckBox v-model="v$.personalized.$model">
        {{ t('Reviews.reviewReplyDialog.personalizedCheckbox.label') }}
      </CheckBox>
    </div>
    <RichInput
      id="reply-input"
      ref="richInput"
      :key="inputKey"
      v-model="v$.reply.$model"
      :error="getValidationErrors(v$.reply)"
      hide-controls
      plain-text-only
      @focusin="onFocusIn"
      @focusout="onFocusOut"
    />

    <div class="flex items-end justify-between">
      <CheckBox v-model="v$.notifyReviewer.$model">{{ t('Reviews.reviewReplyDialog.informReviewerCheckbox.label') }}</CheckBox>
      <button class="btn-primary-color-s" data-testid="reviews-reply-submit" :disabled="loading || v$.$invalid" @click="onSubmit">
        {{ t('Reviews.reviewReplyDialog.send') }}
      </button>
    </div>
  </ModalDialog>
</template>
<script setup lang="ts">
import { CheckBox, ModalDialog, nl2br, MessageBanner } from '@ramp106/omrjs-core-ui'
import { RichInput } from '@ramp106/omrjs-core-ui/deprecated'
import { useVuelidate, type ValidationArgs } from '@vuelidate/core'
import { storeToRefs } from 'pinia'
import TurnDown from 'turndown'
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useUpsertReviewReplyMutation } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { getValidationErrors } from '@/helpers/validationHelper'
import { required, maxLength, minLength } from '@/services/validators'
import { useMeStore } from '@/stores'

import type { ReviewReplyData, ReviewReplySubject } from './types'

type FormData = {
  reply: string
  personalized: boolean
  notifyReviewer: boolean
}

const REPLY_MIN_LENGTH = 2
const REPLY_MAX_LENGTH = 1200

const props = defineProps<{
  subject: ReviewReplySubject
  reviewId?: string
  universalReviewId?: string
  reviewReply?: ReviewReplyData | null
  entityTitle: string
  detailPageUrl: string
  reviewerFirstName?: string
}>()

const emit = defineEmits(['closed', 'submitted'])

const meStore = useMeStore()
const { me } = storeToRefs(meStore)
const senderName = computed(() => props.reviewReply?.senderName?.split(' ')[0] || me.value?.firstName || '')
const modal = ref<InstanceType<typeof ModalDialog> | null>(null)
const richInput = ref<InstanceType<typeof RichInput> | null>(null)
const inputKey = ref(1)
const focused = ref(false)
const { t } = useI18n()
const { loading, mutate: upsertReviewReply } = useUpsertReviewReplyMutation({ clientId: 'reviews' })

const formData = reactive<FormData>({
  reply: props.reviewReply?.reply || '',
  personalized: props.reviewReply?.personalized || false,
  notifyReviewer: !props.reviewReply,
})

const rules = computed<ValidationArgs<FormData>>(() => ({
  reply: {
    required,
    minLength: minLength(REPLY_MIN_LENGTH),
    maxLength: maxLength(REPLY_MAX_LENGTH),
  },
  personalized: {},
  notifyReviewer: {},
}))

const v$ = useVuelidate(rules, formData)

function open() {
  resetFormData()
  modal.value?.open()
}
function close() {
  modal.value?.close()
}

function onFocusIn() {
  focused.value = true
}

function onFocusOut() {
  setTimeout(() => (focused.value = false), 100)
}

function resetFormData() {
  formData.reply = props.reviewReply?.reply || ''
  formData.personalized = props.reviewReply?.personalized || false
  formData.notifyReviewer = !props.reviewReply
}

function successMessage(): string {
  if (props.subject === 'agency') {
    return props.reviewerFirstName
      ? t('Reviews.agency.reviewReplyDialog.successWithAuthor', {
          reviewerName: props.reviewerFirstName,
          reviewDetailUrl: props.detailPageUrl,
        })
      : t('Reviews.agency.reviewReplyDialog.successWithoutAuthor', { reviewDetailUrl: props.detailPageUrl })
  }
  return props.reviewerFirstName
    ? t('Reviews.reviewReplyDialog.successWithAuthor', { reviewerName: props.reviewerFirstName, reviewsProductUrl: props.detailPageUrl })
    : t('Reviews.reviewReplyDialog.successWithoutAuthor', { reviewsProductUrl: props.detailPageUrl })
}

async function onSubmit() {
  const isFormValid = await v$.value.$validate()
  if (!isFormValid) {
    showNotification(t('Reviews.reviewReplyDialog.invalidForm'), 'error')
    return
  }

  emit('submitted')

  const turnDown = new TurnDown({
    emDelimiter: '_',
    linkStyle: 'inlined',
    headingStyle: 'atx',
  })

  try {
    await upsertReviewReply({
      reply: turnDown.turndown(nl2br(formData.reply)),
      reviewId: props.subject === 'product' ? props.reviewId : undefined,
      universalReviewId: props.subject === 'agency' ? props.universalReviewId : undefined,
      notifyReviewer: formData.notifyReviewer,
      personalized: formData.personalized,
    })
    showNotification(successMessage(), 'success', 10000)
    close()
  } catch (_e) {
    showNotification(t('Reviews.reviewReplyDialog.serverError'), 'error', 10000)
  }
}

defineExpose({ open, close })
</script>
